import * as process from '@/services/core/activityprocess';
import * as attachment from '@/services/core/liuchengrenwusiyoufujian';
import * as task from '@/services/core/processtask';
import * as template from '@/services/core/processtemplate';
import * as stage from '@/services/core/stagecontrolboss';

// Allow renewal before sending, but never replay a write after authentication failure.
export const processRequestOptions = {
  skipErrorHandler: true,
  handleForbiddenLocally: true,
  authSkipRetry: true,
};

export const processReadRequestOptions = {
  ...processRequestOptions,
  authSkipRetry: false,
};

export class ProcessError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly denied = false,
  ) {
    super(message);
    this.name = 'ProcessError';
  }
}

const deniedCodes = new Set([
  '401',
  '403',
  '100401',
  '100403',
  'AUTH_NO_PERMISSION',
  'AUTH_TOKEN_EXPIRED',
  'invalid_token',
  'AUTH_REFRESH_EXPIRED',
  'AUTH_USER_DISABLED',
  'UNAUTHORIZED',
  'FORBIDDEN',
]);
const object = (value: unknown): Record<string, unknown> =>
  (value && typeof value === 'object'
    ? Object.fromEntries(Object.entries(value))
    : {});
export function normalizeProcessError(error: unknown): unknown {
  if (error instanceof ProcessError) return error;
  const value = object(error);
  const response = object(value.response);
  const info = object(value.info || response.data);
  const codes = [info.errorCode, info.uniformErrorCode, response.status]
    .filter((code) => typeof code === 'number' || typeof code === 'string')
    .map(String);
  const denied = codes.find(
    (code) =>
      deniedCodes.has(code) ||
      /FORBIDDEN|UNAUTHORIZED|ACCESS_DENIED/.test(code),
  );
  function getMessage() {
    if (typeof info.errorMessage === 'string') {
      return info.errorMessage;
    }
    if (typeof info.uniformErrorMsg === 'string') {
      return info.uniformErrorMsg;
    }
    return errorText(error);
  }
  const message = getMessage();
  if (denied) return new ProcessError(message, denied, true);
  if (
    typeof response.status === 'number' &&
    (response.status >= 500 || response.status === 408)
  ) return error;
  if (
    value.name === 'BizError' ||
    (typeof response.status === 'number' &&
      response.status >= 400 &&
      response.status < 500)
  ) return new ProcessError(message, codes[0]);
  return error;
}

type Envelope = Pick<
CoreAPI.BaseResponse,
| 'success'
| 'errorCode'
| 'errorMessage'
| 'uniformErrorCode'
| 'uniformErrorMsg'
>;
export async function checked<T>(
  request: Promise<Envelope & { data?: T }>,
): Promise<T> {
  const response = await request.catch((error: unknown) => {
    throw normalizeProcessError(error);
  });
  if (response.success === false) throw normalizeProcessError({ name: 'BizError', info: response });
  if (response.success !== true) throw new Error('服务未返回可确认的结果，请核实后再继续');
  if (response.data === undefined || response.data === null) throw new Error('服务未返回完整结果，请核实后再继续');
  return response.data;
}

function consume<B, T>(
  operation: (
    body: B,
    options?: typeof processRequestOptions & { signal?: AbortSignal },
  ) => Promise<Envelope & { data?: T }>,
  options = processRequestOptions,
) {
  return (body: B, signal?: AbortSignal) =>
    checked(operation(body, { ...options, signal }));
}

export const processService = {
  entry: consume(
    process.getBossActivityProcessEntry,
    processReadRequestOptions,
  ),
  page: consume(process.pageBossActivityProcesses, processReadRequestOptions),
  get: consume(process.getBossActivityProcess, processReadRequestOptions),
  save: consume(process.saveBossActivityProcessDefinition),
  validate: consume(
    process.validateBossActivityProcess,
    processReadRequestOptions,
  ),
  lifecycle: consume(process.executeBossActivityProcessLifecycle),
  grants: consume(process.saveBossActivityProcessGrants),
  outdoor: consume(process.setBossActivityProcessOutdoor),
  rework: consume(process.createBossActivityProcessRework),
  assignController: consume(process.assignBossActivityProcessController),
  correctTimes: consume(process.correctBossActivityProcessStageTimes),
  history: consume(
    process.pageBossActivityProcessHistory,
    processReadRequestOptions,
  ),
  commandResult: consume(
    process.getBossActivityProcessCommandResult,
    processReadRequestOptions,
  ),
  templatePage: consume(
    template.pageBossProcessTemplates,
    processReadRequestOptions,
  ),
  templateGet: consume(
    template.getBossProcessTemplate,
    processReadRequestOptions,
  ),
  templateSave: consume(template.saveBossProcessTemplateDraft),
  templatePublish: consume(template.publishBossProcessTemplate),
  templateFromProcess: consume(template.createBossProcessTemplateFromProcess),
  taskGet: consume(task.getBossProcessTask, processReadRequestOptions),
  taskCommand: consume(task.executeBossProcessTaskCommand),
  stageGet: consume(
    stage.getBossStageControlSnapshot,
    processReadRequestOptions,
  ),
  stageCommand: consume(stage.executeBossStageControlCommand),
  attachmentInit: consume(attachment.initBossProcessTaskAttachment),
  attachmentComplete: consume(attachment.completeBossProcessTaskAttachment),
  attachmentAccess: consume(
    attachment.getBossProcessTaskAttachmentAccess,
    processReadRequestOptions,
  ),
  async attachmentCancel(body: CoreAPI.ProcessAttachmentUploadRequest) {
    const result = await attachment
      .cancelBossProcessTaskAttachment(body, processRequestOptions)
      .catch((error: unknown) => {
        throw normalizeProcessError(error);
      });
    if (result.success === false) throw normalizeProcessError({ name: 'BizError', info: result });
    if (result.success !== true) throw new Error('取消结果未确认');
  },
};

export function errorText(error: unknown): string {
  return error instanceof Error ? error.message : '请求失败，请重试查询';
}

export function isAccessFailure(error: unknown): boolean {
  const normalized = normalizeProcessError(error);
  return (
    normalized instanceof ProcessError &&
    (normalized.denied ||
      deniedCodes.has(normalized.code || '') ||
      /FORBIDDEN|UNAUTHORIZED|ACCESS_DENIED|AUTHORIZATION|TOKEN/.test(
        normalized.code || '',
      ))
  );
}

export function permits(
  capabilities: CoreAPI.ActivityProcessCapabilities | undefined,
  action: string,
): boolean {
  return (
    capabilities?.canView === true &&
    capabilities.allowedActions?.includes(action) === true
  );
}

export function requiredId(
  value: string | undefined,
  label = '资源标识',
): string {
  if (!value) throw new ProcessError(`${label}缺失，请重新加载`);
  return value;
}
